import { Injectable, inject } from '@angular/core';
import { Observable, of, tap } from 'rxjs';
import { QepFileBrowserService } from '@app/modules/documents/services/qep-file-browser.service';
import { IAttachment } from '@common/models/IAttachment';
import { IRepositoryItem } from '@common/models/IRepositoryItem';
import { pdfTitleChange } from '@app/services/common/utils';
import { QuangularToasterService } from '@app/services/common/toaster.service';

/** Extension -> MIME. Was declared three times over: in document-repository,
 *  in withheld-decision, and again here. */
export const MIME_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain', csv: 'text/csv', png: 'image/png', jpeg: 'image/jpeg', jpg: 'image/jpeg'
};

/**
 * Opening a Quilt document in the shared "quilt_preview" popup.
 *
 * THE SAME FOUR STEPS WERE WRITTEN THREE TIMES -- fetch the blob, make an
 * object URL, retitle it so the popup is not named after a GUID, open it --
 * in document-repository (the Documents tree), in withheld-decision, and in
 * the quarter-summary panel. Three copies of a blob lifecycle and an error
 * toast is three places for them to drift apart, so they all call this.
 *
 * Two entry points, because the callers hold different things:
 *   openAttachment - the caller already knows the file's path (the Documents
 *                    tree does: it IS the file list)
 *   openByName     - the caller holds only a file NAME and the company, so
 *                    the path has to be resolved first (the analytics
 *                    screens, which read a name out of the pipeline database)
 */
@Injectable({ providedIn: 'root' })
export class FilePreviewService {
  private readonly fileBrowserService = inject(QepFileBrowserService);
  private readonly toaster = inject(QuangularToasterService);
  private readonly pcFilesCache = new Map<string, IRepositoryItem[]>();

  /** Open a file whose path is already known.
   *
   *  `onSettled` fires on success AND on failure, so a caller clears its
   *  spinner in one place. Errors are toasted here rather than returned: all
   *  three callers showed the same message. */
  openAttachment(attachment: IAttachment, onSettled?: () => void): void {
    this.fileBrowserService.previewFile(attachment).subscribe({
      next: async (blob: Blob) => {
        onSettled?.();
        const fileURL = URL.createObjectURL(blob);
        const newBlob = await pdfTitleChange(fileURL, attachment.fileName, attachment.mimeType);
        const newFileURL = URL.createObjectURL(newBlob);
        window.open(newFileURL, 'quilt_preview',
          'width=800,height=1024,popup=yes,scrollbars=yes,resizable=yes');
      },
      error: () => {
        onSettled?.();
        this.toaster.showToast({ message: 'File preview failed.', status: 'error' });
      }
    });
  }

  /** Open a file by NAME, resolving it against the company's documents first.
   *
   *  The file list is cached per company: a reader checking several cells on
   *  one company otherwise re-fetched the whole repository listing per click. */
  openByName(pc: string, fileName: string, onSettled?: () => void): void {
    this.pcFiles(pc).subscribe({
      next: (files) => {
        const match = files.find((f) => f.storageStat.name === fileName);
        if (!match) {
          onSettled?.();
          this.toaster.showToast({
            message: `${fileName} was not found in this company's documents.`, status: 'error' });
          return;
        }
        const ext = match.storageStat.name.split('.').pop()?.toLowerCase() ?? '';
        this.openAttachment({
          fileName: match.storageStat.name,
          path: match.storageStat.path,
          mimeType: MIME_TYPES[ext],
          index: 0,
          attachmentType: match.properties?.documentType
        }, onSettled);
      },
      error: () => {
        onSettled?.();
        this.toaster.showToast({
          message: 'Could not load this company\'s documents.', status: 'error' });
      }
    });
  }

  private pcFiles(pc: string): Observable<IRepositoryItem[]> {
    const cached = this.pcFilesCache.get(pc);
    if (cached) return of(cached);
    return this.fileBrowserService.getPcFiles(pc).pipe(tap((f) => this.pcFilesCache.set(pc, f)));
  }
}
